import { ChangeEvent, JSX, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    Stack,
    TextField,
    Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { AdminMiniDescriptor, AdminMiniSendReport, AdminMiniTarget } from '../admin-mini.types';
import { AdminMiniSender, AdminMiniSenderPanel } from './AdminMiniSenderPanel';
import { AdminMiniSchedule, AdminMiniSchedulePanel } from './AdminMiniSchedulePanel';
import { AdminMiniTargetList } from './AdminMiniTargetList';
import { AdminMiniMessageEditor } from './AdminMiniMessageEditor';
import { AdminMiniAccess } from '../hooks/admin-mini-access';
import { isReadyToSend, runBlocker } from '../admin-mini.rules';
import { AdminMiniCompanyPicker } from './AdminMiniCompanyPicker';
import { AdminMiniNokPanel } from './AdminMiniNokPanel';
import { ChosenNok } from '../hooks/admin-mini-noks';

export interface AdminMiniRunDraft {
    name: string;
    sender: AdminMiniSender;
    schedule: AdminMiniSchedule;
    targets: AdminMiniTarget[];
    /** The company this run is about, or '' when the admin is typing its details instead. */
    companyId: string;
    /** What scores the replies. A run with none is never scored at all. */
    noks: ChosenNok[];
}

export interface AdminMiniComposerProps {
    tool: AdminMiniDescriptor;
    draft: AdminMiniRunDraft;
    onChange: (draft: AdminMiniRunDraft) => void;
    onActivate: () => void;
    activating: boolean;
    access: AdminMiniAccess;
    /** The outcome of the last activation, shown above the form. */
    report?: AdminMiniSendReport;
    /** Rendered under the editor. The assistant's draft offer goes here. */
    editorFooter?: JSX.Element;
}

/** A blank run, so a caller does not have to remember the shape. */
export function emptyDraft(tool: AdminMiniDescriptor): AdminMiniRunDraft {
    return {
        name: '',
        sender: {
            senderMode: tool.senderModes[0],
            nokChoice: 'random',
            fromNok: '',
            fromChoice: 'random',
            fromPersona: '',
            fromName: '',
            fromEmail: '',
        },
        schedule: { scheduleType: 'now', scheduledAt: '', startDate: '', endDate: '' },
        targets: [],
        companyId: '',
        noks: [],
    };
}

/**
 * Assembling one admin run: who it goes to, what it says, who from, and when.
 *
 * Two columns, and which side a thing sits on is the decision worth knowing about. **The left is
 * the run and the right is the message.** Everything on the left is settled once and applies to
 * every recipient; everything on the right is written per recipient and changes as the admin moves
 * down the list. The older Mystery Shopper form interleaved them, so the Nok picker sat between two
 * recipient controls and reading the form top to bottom told you nothing about what applied where.
 *
 * **Nothing here activates without the permission to.** The button is hidden rather than disabled
 * when the role lacks it, because a disabled control invites the admin to go looking for the thing
 * that would enable it, and there is nothing they can do.
 */
export const AdminMiniComposer: (props: AdminMiniComposerProps) => JSX.Element =
    (props: AdminMiniComposerProps): JSX.Element => {
        const { draft, onChange, tool, access } = props;
        const [editingEmail, setEditingEmail] = useState<string>('');

        const editing: AdminMiniTarget | undefined = draft.targets.find(
            (target: AdminMiniTarget): boolean => target.email === editingEmail
        );

        const ready: AdminMiniTarget[] = draft.targets.filter(isReadyToSend);

        function updateTarget(updated: AdminMiniTarget): void {
            onChange({
                ...draft,
                targets: draft.targets.map((target: AdminMiniTarget): AdminMiniTarget =>
                    target.email === updated.email ? updated : target),
            });
        }

        const blocked: string = runBlocker(draft);

        return (
            <Stack spacing={ 2.5 } sx={ { width: '100%' } }>
                { props.report !== undefined && (
                    <Alert severity={ props.report.status === 'success' ? 'success' : 'error' }
                           sx={ { fontSize: '13px' } }>
                        { props.report.message }
                    </Alert>
                ) }

                <TextField fullWidth
                           size="small"
                           label={ `What is this ${ tool.runNoun } for?` }
                           placeholder="A name you will recognise on the list"
                           value={ draft.name }
                           disabled={ props.activating }
                           onChange={ (e: ChangeEvent<HTMLInputElement>): void =>
                               onChange({ ...draft, name: e.target.value }) } />

                <Box sx={ {
                    display: 'grid',
                    // One column below roughly a tablet, where two columns of this leave neither
                    // usable. `minmax(0, …)` because a grid child's default `min-width: auto`
                    // lets the editor's toolbar push the column wider than its track.
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 360px) minmax(0, 1fr)' },
                    gap: '24px',
                    alignItems: 'start',
                } }>
                    <Stack spacing={ 3 } divider={ <Divider flexItem /> }>
                        <AdminMiniSenderPanel tool={ tool }
                                              value={ draft.sender }
                                              disabled={ props.activating }
                                              onChange={ (sender: AdminMiniSender): void =>
                                                  onChange({ ...draft, sender: sender }) } />

                        <AdminMiniSchedulePanel value={ draft.schedule }
                                                runNoun={ tool.runNoun }
                                                disabled={ props.activating }
                                                onChange={ (schedule: AdminMiniSchedule): void =>
                                                    onChange({ ...draft, schedule: schedule }) } />

                        <AdminMiniCompanyPicker value={ draft.companyId }
                                                disabled={ props.activating }
                                                onChange={ (companyId: string): void =>
                                                    onChange({ ...draft, companyId: companyId }) } />

                        <AdminMiniNokPanel systemCode={ tool.systemCode }
                                           companyId={ draft.companyId }
                                           chosen={ draft.noks }
                                           disabled={ props.activating }
                                           onChange={ (noks: ChosenNok[]): void =>
                                               onChange({ ...draft, noks: noks }) } />

                        <AdminMiniTargetList targets={ draft.targets }
                                             editingEmail={ editingEmail }
                                             disabled={ props.activating }
                                             onEdit={ (target: AdminMiniTarget): void =>
                                                 setEditingEmail(target.email) }
                                             onChange={ (targets: AdminMiniTarget[]): void => {
                                                 // A removed recipient must not stay open in the
                                                 // editor, or the next keystroke writes to nobody.
                                                 if (!targets.some((t: AdminMiniTarget): boolean => t.email === editingEmail)) {
                                                     setEditingEmail('');
                                                 }
                                                 onChange({ ...draft, targets: targets });
                                             } } />
                    </Stack>

                    { /* Sticky, because the left column is much taller than this one and an
                         admin writing a message scrolls the recipient list past it. Only above
                         `md`, where the two are side by side; stacked, sticking would pin the
                         editor over the controls beneath it. */ }
                    <Box sx={ { position: { xs: 'static', md: 'sticky' }, top: '8px' } }>
                        <AdminMiniMessageEditor target={ editing }
                                                disabled={ props.activating }
                                                footer={ props.editorFooter }
                                                onChange={ updateTarget } />
                    </Box>
                </Box>

                <Divider />

                <Box sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' } }>
                    <Box sx={ { fontSize: '13px', color: 'text.secondary' } }>
                        { blocked !== ''
                            ? blocked
                            : `Ready to send to ${ ready.length } of ${ draft.targets.length }.` }
                    </Box>

                    { access.can('run') && (
                        <Tooltip title={ blocked === '' ? 'Sends mail and spends credits' : blocked }>
                            <span>
                                <Button variant="contained"
                                        disabled={ blocked !== '' || props.activating }
                                        onClick={ props.onActivate }
                                        startIcon={ props.activating
                                            ? <CircularProgress size={ 16 } color="inherit" />
                                            : <SendIcon /> }>
                                    { props.activating ? 'Starting' : 'Start this run' }
                                </Button>
                            </span>
                        </Tooltip>
                    ) }
                </Box>
            </Stack>
        );
    };
